import React from 'react';
import Immutable from 'immutable';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { UI_OPERATION_STATUS_SUCCESS } from '@uncinc/react-drupal-core';

import { WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';

import BlockClientInfo from '../../components/Blocks/BlockClientInfo';
import BarGraphOverview from '../../components/RecoveryPlan/BarGraphOverview';
import { DOMAIN_MENTAAL_BALANS, ROUTE_TYPE_GUIDED_SELF_HELP, WEBFORM_FIELD_NURSE_PRACTITIONER_INFO } from '../../config/constants';
import Page from '../../containers/Page';
import useClasses from '../../hooks/useClasses';
import useOperationStatus from '../../hooks/useOperationStatus';
import usePopup from '../../hooks/usePopup';
import useRoutes from '../../hooks/useRoutes';
import useToast from '../../hooks/useToast';
import useTypedDispatch from '../../hooks/useTypedDispatch';
import useDailyRegistrations from '../../hooks/useDailyRegistrations';
import {
  Loader,
  PhaseTeaser,
  Popup,
  SegmentedControl,
} from '../../components';

import {
  fetchClientExercises,
  fetchClientRegistrations,
  fetchProgram,
} from '../../store/actions/NursePractitioner';

import './style.scss';

const PageClientDetails = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const routes = useRoutes(ROUTE_TYPE_GUIDED_SELF_HELP);
  const toast = useToast();
  const dispatch = useTypedDispatch();
  const popup = usePopup('answers', {
    closeOnOutsideClick: true,
    closeOnEscape: true,
  });

  const { buildClass } = useClasses('PageClientDetails');

  // Get domain and uid from path.
  const currentPath = useSelector((state) => state.getIn(['router', 'location', 'pathname']));
  const { domain, uid } = parseParams(currentPath);

  // Operation statuses
  const programOperationStatus = useOperationStatus('nursePractitioner', `fetchFields-${domain}`);
  const clientRegistrationsOperationStatus = useOperationStatus('nursePractitioner', `fetchClientRegistrations-${domain}-${uid}`);
  const clientExercisesOperationStatus = useOperationStatus('nursePractitioner', `fetchClientExercises-${domain}-${uid}`);
  let isDoneLoadingProgress = programOperationStatus === UI_OPERATION_STATUS_SUCCESS
    && clientExercisesOperationStatus === UI_OPERATION_STATUS_SUCCESS;

  // Get current client from the state.
  const availableClients = useSelector((state) => state.getIn(['nursePractitioner', 'clients'], Immutable.List()));
  const client = availableClients.find((availableClient) => availableClient.get('domain') === domain && availableClient.get('uid') === uid);

  // Get client details from state.
  const program = useSelector((state) => state.getIn(['nursePractitioner', 'program', domain], Immutable.List()));
  const streaks = useSelector((state) => state.getIn(['nursePractitioner', 'clientDetails', domain, uid, 'streaks'], {}));
  const completedExercises = useSelector((state) => state.getIn(['nursePractitioner', 'clientDetails', domain, uid, 'completed_exercises'], Immutable.Map())).toJS();
  const clientDomain = client ? client.get('domain') : null;

  // Determine if registrations should be hidden and if so, update isDoneLoadingProgress.
  const hideRegistrations = clientDomain === DOMAIN_MENTAAL_BALANS;
  if (hideRegistrations) {
    isDoneLoadingProgress = programOperationStatus === UI_OPERATION_STATUS_SUCCESS;
  }

  const {
    registrations,
    registrationsGroupedByWeek,
    highestAmountOrGoal,
    currentWeekNumber,
    weeksBeforeProgramStartDate,
  } = useDailyRegistrations(
    ['nursePractitioner', 'clientDetails', domain, uid, 'registrations'],
    ['nursePractitioner', 'clients', availableClients.indexOf(client), 'program_start_date'],
  );

  // Define tabs.
  const tabOptions = {
    exercises: t('pages.client_details.exercises'),
    progress: t('pages.client_details.progress'),
  };
  const [currentTab, setTab] = React.useState('exercises');

  // Currently viewing.
  const [currentlyViewing, setCurrentlyViewing] = React.useState();
  const currentlyViewingDetails = React.useMemo(() => {
    if (!currentlyViewing || !program) {
      return null;
    }

    const currentExercise = program
      .find((phase) => phase.keySeq().includes(currentlyViewing))
      .get(currentlyViewing, Immutable.Map());

    return {
      title: currentExercise.get('title', ''),
      pohDescription: currentExercise.get('poh_description', ''),
    };
  }, [program, currentlyViewing]);

  // Helper functions.
  function recursivelyFlattenFieldDefinitions(webformId, fields, acc = {}) {
    const keys = getEligibleKeys(fields);

    if (!keys || !keys.length) {
      return acc;
    }

    keys.forEach((key) => {
      const currentItem = fields[key];

      acc[key] = {
        title: currentItem['#title'],
        text: currentItem['#text'],
        answer: completedExercises[webformId].data[key],
      };

      acc = {
        ...acc,
        ...recursivelyFlattenFieldDefinitions(webformId, currentItem, acc),
      };
    });

    return acc;
  }

  function checkIfExerciseDone(exerciseId) {
    const completedExercisesKeys = Object.keys(completedExercises);

    return completedExercisesKeys.includes(exerciseId);
  }

  function fetchFields(webformId) {
    if (program && Object.keys(completedExercises).length) {
      const phaseWithWebform = program.find((phase) => {
        const webformIds = phase.keySeq();
        return webformIds.includes(webformId);
      });

      if (!phaseWithWebform) {
        return;
      }

      const exerciseFields = phaseWithWebform.getIn([webformId, 'fields']).toJS();

      return recursivelyFlattenFieldDefinitions(webformId, exerciseFields);
    }

    return {};
  }

  const fieldDefinitions = fetchFields(currentlyViewing);

  React.useEffect(() => {
    async function loadClientRegistrations() {
      if (!hideRegistrations && client && !clientRegistrationsOperationStatus) {
        try {
          await dispatch(fetchClientRegistrations(domain, uid));
        } catch (err) {
          console.error(err);
          toast.show(t('pages.client_details.fetch_error'), 'error');
        }
      }
    }
    loadClientRegistrations();
  }, [client, !clientRegistrationsOperationStatus]);

  React.useEffect(() => {
    async function loadClientExercises() {
      if ((Object.keys(registrations).length > 0 && !clientExercisesOperationStatus) ||
        (hideRegistrations && !clientExercisesOperationStatus)) {
        try {
          await dispatch(fetchClientExercises(domain, uid));
        } catch (err) {
          console.error(err);
          toast.show(t('pages.client_details.fetch_error'), 'error');
        }
      }
    }
    loadClientExercises();
  }, [registrations, clientExercisesOperationStatus]);

  React.useEffect(() => {
    async function loadProgram() {
      if (domain && !programOperationStatus) {
        try {
          await dispatch(fetchProgram(domain));
        } catch (err) {
          console.error(err);
          toast.show(t('pages.client_details.fetch_error'), 'error');
        }
      }
    }

    loadProgram();
  }, [domain, programOperationStatus]);

  return (
    <Page className={buildClass()} title={t('pages.client_details.title')}>
      <div className="col-12 col-md-5 col-lg-4 order-md-2 py-8 py-lg-12">
        {isDoneLoadingProgress && (
          <BlockClientInfo
            uid={client.get('uid')}
            avatar={client.get('avatar', 1)}
            name={client.get('name')}
            nickname={client.get('nickname')}
            domain={client.get('domain')}
            totalRegistrations={streaks.total || 0}
            completedRegistrations={streaks.completed || 0}
            successes={streaks.success || 0}
            fails={streaks.failed || 0}
            onUnpairSuccess={() => {
              history.push(routes.clients);
            }}
            hideRegistrations
          />
        )}
      </div>
      <div className="col-12 col-md-7 col-lg-6 py-8 py-lg-12">
        {(popup.isOpen && currentlyViewingDetails) && (
          <Popup
            onClose={() => {
              popup.close();
              setCurrentlyViewing(null);
            }}
            title={currentlyViewingDetails.title}
            ref={popup.popupProps.ref}
          >
            <>
              {currentlyViewingDetails.pohDescription.length > 0 && (
                <div className={buildClass('poh-description')}>
                  <WysiwygField
                    property="schema:text"
                    html={currentlyViewingDetails.pohDescription}
                  />
                </div>
              )}
              {Object.keys(fieldDefinitions).map((key) => {
                const currentItem = fieldDefinitions[key];

                return (
                  <div className={buildClass('field-item')} key={`field-item-${key}`}>
                    {(key !== WEBFORM_FIELD_NURSE_PRACTITIONER_INFO && currentItem.title && (currentItem.text || currentItem.answer)) && (
                      <h3>{currentItem.title}</h3>
                    )}

                    {currentItem.text && (
                      <WysiwygField
                        property="schema:text"
                        html={currentItem.text}
                        className={buildClass('prose')}
                      />
                    )}

                    {currentItem.answer && (
                      <div className={key === WEBFORM_FIELD_NURSE_PRACTITIONER_INFO && 'italic'}>
                        {Array.isArray(currentItem.answer) ? (
                          currentItem.answer.map((item) => (
                            <div>{item}</div>
                          ))
                        ) : (
                          <div>{currentItem.answer}</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          </Popup>
        )}
        {/* Exercises <> registrations switch */}
        {isDoneLoadingProgress && !hideRegistrations && (
          <SegmentedControl options={tabOptions} selected={currentTab} onChange={setTab} />
        )}
        <div role="tabpanel" aria-labelledby={`segmented-control_${currentTab}`}>
          {/* Show daily registrations */}
          {(currentTab === 'progress' && !hideRegistrations) && (
            <BarGraphOverview
              readonly
              registrationsGroupedByWeek={registrationsGroupedByWeek}
              highestAmountOrGoal={highestAmountOrGoal}
              currentWeekNumber={currentWeekNumber}
              weeksBeforeProgramStartDate={weeksBeforeProgramStartDate}
            />
          )}
          {/* Show exercises */}
          {(currentTab === 'exercises' || hideRegistrations) && (
            isDoneLoadingProgress ? (
              program.toJS().map((phase, index) => {
                const phaseNumber = index + 1;
                const exerciseIds = Object.keys(phase);

                const phaseData = {};

                exerciseIds.forEach((exerciseId) => {
                  const isComplete = checkIfExerciseDone(exerciseId);
                  phaseData[exerciseId] = {
                    exerciseId,
                    ...phase[exerciseId],
                    isComplete,
                  };
                });

                return (
                  <PhaseTeaser
                    key={`phase-${phaseNumber}`}
                    phaseNumber={phaseNumber}
                    exercises={phaseData}
                    onClick={(exercise) => {
                      if (exercise.isComplete) {
                        setCurrentlyViewing(exercise.exerciseId);
                        popup.open();
                      }
                    }}
                  />
                );
              })
            ) : (
              <Loader />
            )
          )}
        </div>
      </div>
    </Page>
  );
};

function parseParams(url) {
  const paramsAsArray = url.split('/').slice(2);

  return {
    domain: paramsAsArray[0],
    uid: paramsAsArray[1],
  };
}

function getEligibleKeys(webformData) {
  const keys = Object.keys(webformData)
    // filter out keys starting with pound signs
    .filter((key) => key.at(0) !== '#')
    // filter out keys that aren't objects
    .filter((key) => {
      const property = webformData[key];
      const isObject = typeof property === 'object' && !Array.isArray(property);
      return isObject;
    });

  return keys;
}

export default PageClientDetails;
